import React, { useEffect, useState } from "react";
import TableHeader from "../../common/MagicPageHeader";
import Header from "../../common/MagicTableHeader";
import MagicTable from "../../common/MagicTable";
import DownloadToXlsxFile from "../../../helper/DownloadToXlsxFile";
import ImageComponent from "../../mui/Image";
import { useFetchStakeholdersFeedbackReportsQuery } from "../../../store/apis/reportsApis";

interface TabCategory {
  id: number;
  name: string;
  code: string;
}

interface StakeholdersFeedbackTableProps {
  selectedTab: TabCategory | null;
  magicPageHeaderBtn: {
    text: string;
    variant: string;
    startIcon: React.ReactNode;
    onClick: () => void;
    sx: Record<string, string>;
  }[];
  magicTableHeaderBtn: any[];
  params:any;
  isInvalidSearch: boolean;
  onPaginationChange: (pageNo: number) => void;
  onSearch: (searchTerm: string) => void;
}

const StakeholdersFeedbackTable: React.FC<StakeholdersFeedbackTableProps> = ({
  selectedTab,
  magicTableHeaderBtn,
  params,
  isInvalidSearch,
  onPaginationChange,
  onSearch
}) => {
  const [downloadParams, setDownloadParams] = useState<any>(null);
  const [downloadTrigger, setDownloadTrigger] = useState(false);
  const columns = [
    {
      field: "region",
      headerName: "Region",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "funder",
      headerName: "Funder",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "centre",
      headerName: "Centre",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "centreBusinessType",
      headerName: "Center business type",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "state",
      headerName: "State",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "district",
      headerName: "City",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "cityManager",
      headerName: "City Manager",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "centreStatus",
      headerName: "Centre status",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "stakeholderFeedbackScore",
      headerName: "Stakeholder feedback score",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "stakeholderAuditScore",
      headerName: "Stakeholder audit score",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "youthFeedbackScore",
      headerName: "Youth feedback score",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "employerFeedbackScore",
      headerName: "Employer feedback score",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
  ];

  const {
    data: fetchedReports = {
      reports: [],
      totalReports: 0,
    },
    isLoading,
  } = useFetchStakeholdersFeedbackReportsQuery(params);
  const { data: downloadData, isLoading: downloadLoading } =
    useFetchStakeholdersFeedbackReportsQuery(downloadParams, {
      skip: !downloadParams,
    });

  const handleDownload = () => {
    const downloadPayload = {
      ...params,
      pageNo: "1",
      pageSize: fetchedReports?.totalReports || "10",
    };
    setDownloadParams(downloadPayload);
    setDownloadTrigger(true); 
  };

  useEffect(() => {
    if (downloadData && downloadTrigger) {
      const sheetData = [
        [
          "Region",
          "Funder",
          "Centre",
          "Centre Business Type",
          "State",
          "City",
          "City Manager",
          "Centre Status",
          "Stakeholder Feedback Score",
          "Stakeholder Audit Score",
          "Youth Feedback Score",
          "Employer Feedback Score",
        ],
        ...(downloadData?.reports.map((feedback: any) => [
          feedback.region || "-",
          feedback.funder || "-",
          feedback.centre || "-",
          feedback.centreBusinessType || "-",
          feedback.state || "-",
          feedback.district || "-",
          feedback.cityManager || "-",
          feedback.centreStatus || "-",
          feedback.stakeholderFeedbackScore || "-",
          feedback.stakeholderAuditScore || "-",
          feedback.youthFeedbackScore || "-",
          feedback.employerFeedbackScore || "-",
        ]) || []),
      ];

      DownloadToXlsxFile({
        fileNamePrifix: "Stakeholders-Feedback-Report",
        sheetData,
      });
      setDownloadTrigger(false);  
    }
    }, [downloadData, downloadTrigger]);  

  const magicPageHeaderBtn = [
    {
      text: "Download",
      variant: "outlined",
      startIcon: (
        <ImageComponent src="/assets/images/login/exportIcon.svg" alt="Export Icon" />
      ),
      onClick: handleDownload,
      loading: downloadLoading,
      sx: {
        background: "white",
        border: "1px solid #D0D5DD",
        borderRadius: "8px",
        color: "black",
      },
    },
  ];
  const totalPages = Math.max(1, Math.ceil((fetchedReports?.totalReports || 0) /10));
  return (
    <>
      <TableHeader
        title={`${selectedTab?.name || "Stakeholders feedback"} Report`}
        subTitle="Here you can view the report with all the details."
        buttons={magicPageHeaderBtn}
        chipLabel={`${fetchedReports?.totalReports || 0} entries`}
      />
      <Header
        buttons={magicTableHeaderBtn}
        onSearchfieldChange={(value) => onSearch(String(value))}
        isInvalidSearch={isInvalidSearch}
      />
      <MagicTable
        columns={columns}
        rows={fetchedReports?.reports}
        loading={isLoading}
        handlePageChange={(event, pageNo) => onPaginationChange(pageNo)}
        currentPage={parseInt(params?.pageNo) || 1}
        count={totalPages}
        rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
        headerSx={{
          paddingLeft: "16px",
          textAlign: "start",
          fontWeight: 600,
          color: "#242424",
          padding: "13px 16px",
        }}
        tableSx={{ minHeight: "300px", width: "100%" }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#DADADA",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 550,
            fontSize: "14px",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },}}
      />
    </>
  );
};

export default StakeholdersFeedbackTable;